import { useLazyQuery, useMutation } from '@apollo/client';
import {
	Table,
	IconButton,
	Icon,
	TableColumnType,
	TableOrderByType,
	Tooltip,
	TableInstance,
	SelectFilterDefinitionType,
	FilterMenuOutputType,
} from '@elipssolution/harfang';
import { mdiLock, mdiEarth, mdiDelete, mdiAlertCircle } from '@mdi/js';
import { styled } from '@mui/material';
import { PhoneNumberUtil, PhoneNumberFormat } from 'google-libphonenumber';
import { useCallback, useMemo, MouseEvent } from 'react';

import { useConfirmationDialogContext } from '../../../components/ConfirmationDialogProvider';
import { useSession } from '../../../src/components/session/SessionContext';
import { PermissionEnum } from '../../../types/permission';
import { emailPattern } from '../../../utils/emailPattern';
import {
	FetchSignContactsType,
	FETCH_SIGN_CONTACTS,
	RemoveSignContactType,
	RemoveSignContactVariablesType,
	REMOVE_SIGN_CONTACT,
} from '../api/contact';
import { SignContactType } from '../types/contact';

const ErrorBox = styled('div')<{ severity: 'error' | 'warning' }>(
	({ theme: { palette, spacing, shape }, severity }) => {
		const backgroundColor = {
			error: `${palette.error.main}3A`,
			warning: `${palette.warning.main}3A`,
		}[severity];

		return {
			backgroundColor,
			padding: spacing(0.4, 0.8),
			display: 'flex',
			flexDirection: 'row',
			gap: spacing(0.7),
			borderRadius: shape.borderRadius,
			color: palette[severity].main,
		};
	},
);

const MissingInformation = () => (
	<Tooltip content="L'information est manquante">
		<ErrorBox severity="error">
			<Icon path={mdiAlertCircle} size="small" color="error" />
			Manquant
		</ErrorBox>
	</Tooltip>
);

const IncorrectlyFormatted = () => (
	<Tooltip content="Le format n'est pas bon">
		<ErrorBox severity="warning">
			<Icon path={mdiAlertCircle} size="small" color="warning" />
			Incorrect
		</ErrorBox>
	</Tooltip>
);

const phoneUtil = PhoneNumberUtil.getInstance();

const evaluatePhoneNumber = (phoneNumber?: SignContactType['phoneNumber']) => {
	if (!phoneNumber) {
		return <MissingInformation />;
	}

	try {
		const parsedNumber = phoneUtil.parseAndKeepRawInput(phoneNumber);

		if (!phoneUtil.isValidNumber(parsedNumber)) {
			return <IncorrectlyFormatted />;
		}

		return phoneUtil.format(parsedNumber, PhoneNumberFormat.INTERNATIONAL);
	} catch (error) {
		return <IncorrectlyFormatted />;
	}
};

const evaluateEmail = (email: SignContactType['email']) =>
	!emailPattern.test(email) ? <IncorrectlyFormatted /> : email;

const baseColumns: TableColumnType<SignContactType>[] = [
	{
		key: 'isPrivate',
		field: 'isPrivate',
		width: 24,
		flexGrow: 0,
		align: 'center',
		render: ({ isPrivate }) => (
			<Tooltip content={isPrivate ? 'Privé' : 'Public'}>
				<Icon path={isPrivate ? mdiLock : mdiEarth} />
			</Tooltip>
		),
	},
	{
		key: 'name',
		field: 'lastName',
		width: 200,
		title: 'Nom',
		sortable: true,
		render: ({ firstName, lastName }) => `${lastName} ${firstName}`,
	},
	{
		key: 'email',
		field: 'email',
		width: 250,
		title: 'Email',
		sortable: true,
		render: ({ email }) => evaluateEmail(email),
	},
	{
		key: 'phoneNumber',
		field: 'phoneNumber',
		width: 115,
		title: 'Téléphone',
		render: ({ phoneNumber }) => evaluatePhoneNumber(phoneNumber),
	},
];

type SignContactsFilterType = {
	isPrivate: SelectFilterDefinitionType<boolean>;
};

const contactsFilters: SignContactsFilterType = {
	isPrivate: {
		label: 'Visibilité',
		type: 'select',
		options: [true, false],
		renderOption: (isPrivate) => (isPrivate ? 'Privé' : 'Public'),
		renderValue: (isPrivate) => (isPrivate ? 'Privé' : 'Public'),
	},
};

type ContactsTableProps = {
	onContactSelection?: (contact: SignContactType) => void;
	tableInstance: TableInstance;
};

const ContactsTable = ({ onContactSelection, tableInstance }: ContactsTableProps) => {
	const { checkPermission } = useSession();
	const { confirmAction } = useConfirmationDialogContext();

	const [fetchSignContacts] = useLazyQuery<FetchSignContactsType>(FETCH_SIGN_CONTACTS);

	const [removeSignContact] = useMutation<RemoveSignContactType, RemoveSignContactVariablesType>(REMOVE_SIGN_CONTACT);

	const signContactsDataSource = useCallback(
		async (
			limit: number,
			offset: number,
			search?: string,
			orderBy?: TableOrderByType<SignContactType>,
			filters?: FilterMenuOutputType<SignContactsFilterType>,
		): Promise<{
			count: number;
			items: SignContactType[];
		}> => {
			const { field, order } = orderBy || {};

			const { data, error: queryError } = await fetchSignContacts({
				variables: {
					page: {
						limit,
						offset,
					},
					search,
					filters,
					...(orderBy && { orderBy: { field, order } }),
				},
			});

			if (queryError) {
				throw queryError;
			}

			const {
				sign_contacts: { count = 0, items = [] },
			} = data ?? {
				sign_contacts: {},
			};

			return {
				count,
				items,
			};
		},
		[fetchSignContacts],
	);

	const handleContactRemovalButtonClick = useCallback(
		async (event: MouseEvent<HTMLButtonElement>, { id, firstName, lastName }: SignContactType) => {
			event.stopPropagation();

			const isRemovalConfirmed = await confirmAction({
				title: `Êtes-vous sûr de vouloir supprimer le contact ${firstName} ${lastName} ?`,
				confirmButtonDetails: {
					color: 'error',
					label: 'Supprimer',
				},
			});

			if (!isRemovalConfirmed) return null;

			const result = await removeSignContact({
				variables: { id },
			});

			tableInstance.reload();

			return result;
		},
		[confirmAction, removeSignContact, tableInstance],
	);

	const columns: TableColumnType<SignContactType>[] = useMemo(
		() => [
			...baseColumns,
			{
				key: 'actions',
				width: 40,
				flexGrow: 0,
				align: 'center',
				render: (contact) => (
					<IconButton onClick={(event) => handleContactRemovalButtonClick(event, contact)}>
						<Icon path={mdiDelete} />
					</IconButton>
				),
			},
		],
		[handleContactRemovalButtonClick],
	);

	return (
		<Table<SignContactType, SignContactsFilterType>
			columns={checkPermission(PermissionEnum.SIGN_CONTACT_WRITE) ? columns : baseColumns}
			dataSource={signContactsDataSource}
			filters={contactsFilters}
			onRowClick={onContactSelection}
			table={tableInstance}
			title="Contacts"
			enableSearch
		/>
	);
};

export default ContactsTable;
